const BASE = '/api';

// Connection state between the caller's company and another company (LinkedIn-style).
// 'following' is the individual-account fallback (simple follow, no request model).
export type ConnectionState = 'none' | 'pending_out' | 'pending_in' | 'connected' | 'following';

function getToken(): string | null {
  return localStorage.getItem('mt_token');
}

async function request<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts?.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  const data = await res.json() as T & { error?: string };
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data as T;
}

export const api = {
  // Public
  getListings: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<{ listings: any[]; count: number }>(`/public/listings${qs}`);
  },
  getListing: (id: number | string) =>
    request<{ listing: any }>(`/public/listings/${id}`),
  getFilters: () =>
    request<{
      materials: string[];
      origins: string[];
      colors: string[];
      finishes: string[];
      companies: { id: number; name: string; city?: string; country?: string; lots_count?: number }[];
    }>('/public/filters'),
  getPublicStats: () =>
    request<{ published_count: number; seller_count: number; origin_count: number; latest_lots: any[] }>('/public/stats'),
  submitLead: (data: { lot_id: number; buyer_name: string; buyer_email: string; buyer_company?: string; message?: string; turnstile_token?: string }) =>
    request<{ ok: boolean }>('/public/leads', { method: 'POST', body: JSON.stringify(data) }),
  // buyer_name/buyer_email are required for guests; omitted for authenticated individuals
  // (the server ties the offer to their account and pulls name/email from their profile).
  submitOffer: (data: { lot_id: number; buyer_name?: string; buyer_email?: string; buyer_company?: string; message?: string; price_eur?: number; quantity?: number; unit?: string; turnstile_token?: string }) =>
    request<{ ok: boolean }>('/public/offers', { method: 'POST', body: JSON.stringify(data) }),
  submitFeedback: (data: { message: string; page_url: string; turnstile_token?: string }) =>
    request<{ ok: boolean }>('/feedback', { method: 'POST', body: JSON.stringify(data) }),

  // Auth
  login: (email: string, password: string) =>
    request<{ token: string; user: any }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (data: {
    company_name: string; country: string; city: string;
    email: string; password: string;
    first_name: string; last_name: string; phone: string;
    lat?: number; lng?: number;
    turnstile_token?: string;
  }) =>
    request<{ ok: boolean; requires_verification?: boolean; message?: string }>(
      '/auth/register', { method: 'POST', body: JSON.stringify(data) }
    ),
  // Finalizes a Google/Apple signup: the pending token comes from the OAuth
  // callback redirect (#oauth=… on /register) and stands in for email+password.
  oauthComplete: (data: {
    pending_token: string;
    company_name: string; country: string; city: string;
    first_name: string; last_name: string; phone: string;
    lat?: number; lng?: number;
  }) =>
    request<{ token: string; user: any }>(
      '/auth/oauth/complete', { method: 'POST', body: JSON.stringify(data) }
    ),
  // Finalizes an individual (non-company) Google/Apple signup from the register chooser.
  oauthCompleteIndividual: (pending_token: string) =>
    request<{ token: string; user: any }>(
      '/auth/oauth/complete-individual', { method: 'POST', body: JSON.stringify({ pending_token }) }
    ),
  // Individual (non-company) email+password signup → verification email, no company.
  registerIndividual: (data: {
    first_name: string; last_name: string; email: string; password: string; turnstile_token?: string;
  }) =>
    request<{ ok: boolean; requires_verification?: boolean; message?: string }>(
      '/auth/register-individual', { method: 'POST', body: JSON.stringify(data) }
    ),
  resendVerification: (email: string) =>
    request<{ ok: boolean; message: string }>(
      '/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) }
    ),
  getMe: () => request<{ user: any }>('/auth/me'),
  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string; headline?: string; bio?: string }) =>
    request<{ ok: boolean }>('/auth/me', { method: 'PATCH', body: JSON.stringify(data) }),
  // Rename the caller's company (owner only).
  updateCompany: (data: { name: string }) =>
    request<{ ok: boolean }>('/vendor/company', { method: 'PATCH', body: JSON.stringify(data) }),
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: boolean; message: string }>('/auth/change-password', {
      method: 'POST', body: JSON.stringify({ current_password, new_password }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: boolean; message: string }>('/auth/reset-password', {
      method: 'POST', body: JSON.stringify({ token, newPassword }),
    }),

  // Company directory (authenticated) — used by Messages compose autocomplete
  getCompaniesDirectory: () =>
    request<{ companies: { id: number; name: string; country: string; city: string; logo_url: string | null }[] }>(
      '/messages/companies'
    ),

  // Users directory (authenticated) — used by Live Chat per-user picker
  getUsersDirectory: () =>
    request<{ users: {
      id: number; first_name: string | null; last_name: string | null;
      avatar_url: string | null; company_id: number;
      company_name: string; company_city: string; company_country: string;
    }[] }>('/messages/users'),

  // Company locations
  listLocations: () =>
    request<{ locations: { id: number; company_id: number; label: string; city: string; country: string; lat: number; lng: number }[] }>(
      '/locations'
    ),
  createLocation: (data: { label: string; city: string; country: string; lat: number; lng: number }) =>
    request<{ ok: boolean; id: number }>('/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateLocation: (id: number, data: Partial<{ label: string; city: string; country: string; lat: number; lng: number }>) =>
    request<{ ok: boolean }>(`/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLocation: (id: number) =>
    request<{ ok: boolean }>(`/locations/${id}`, { method: 'DELETE' }),

  // Vendor
  getMetrics: () => request<any>('/vendor/metrics'),
  getOperations: () => request<any>('/vendor/operations'),
  getMarket: () => request<{
    summary: { market_lots: number; market_companies: number; your_published: number };
    demand: { material: string; leads: number }[];
    benchmarks: {
      material: string; your_min: number | null; your_max: number | null; your_count: number;
      market_min: number | null; market_avg: number | null; market_max: number | null;
      market_count: number; competitors: number;
    }[];
  }>('/vendor/market'),
  // Push notifications — device token registry (called by the native app shell)
  registerPush: (token: string, platform: string) =>
    request<{ ok: boolean }>('/push/register', { method: 'POST', body: JSON.stringify({ token, platform }) }),
  unregisterPush: (token: string) =>
    request<{ ok: boolean }>('/push/unregister', { method: 'POST', body: JSON.stringify({ token }) }),
  getInventory: () => request<{ lots: any[] }>('/vendor/inventory'),
  createLot: (data: any) =>
    request<{ ok: boolean; id: number }>('/vendor/inventory', { method: 'POST', body: JSON.stringify(data) }),
  updateLot: (id: number, data: any) =>
    request<{ ok: boolean }>(`/vendor/inventory/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLot: (id: number) =>
    request<{ ok: boolean }>(`/vendor/inventory/${id}`, { method: 'DELETE' }),
  publishLot: (id: number, published: boolean) =>
    request<{ ok: boolean }>(`/vendor/inventory/${id}/publish`, { method: 'POST', body: JSON.stringify({ published }) }),
  deleteAccount: () => request<{ ok: boolean }>('/vendor/account', { method: 'DELETE' }),
  exportMyData: async (): Promise<void> => {
    const token = getToken();
    const res = await fetch(`${BASE}/vendor/account/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = res.headers.get('Content-Disposition') || '';
    a.download = cd.match(/filename="(.+?)"/)?.[1] || 'matchmarble-data-export.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  getLeads: () => request<{ leads: any[] }>('/vendor/leads'),
  updateLeadStatus: (id: number, status: string) =>
    request<{ ok: boolean }>(`/vendor/leads/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  // Offers (structured, replace leads)
  getOffers: () => request<{ offers: any[] }>('/offers'),
  createOffer: (data: { lot_id: number; price_eur?: number; quantity?: number; unit?: string; message?: string }) =>
    request<{ ok: boolean; offer_id: number; conversation_id: number | null }>('/offers', { method: 'POST', body: JSON.stringify(data) }),
  actOnOffer: (id: number, data: { action: 'accept' | 'decline' | 'withdraw' | 'counter'; price_eur?: number; quantity?: number; unit?: string; message?: string }) =>
    request<{ ok: boolean; offer_id?: number }>(`/offers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Direct messages — unified person↔company / person↔person / company↔company.
  // People + companies search for the Home search bar.
  searchSocial: (q: string) => request<{ people: any[]; companies: any[] }>(`/social/search?q=${encodeURIComponent(q)}`),
  dmThreads: () => request<{ threads: any[] }>('/dm'),
  dmUnread: () => request<{ count: number }>('/dm/unread'),
  dmThread: (id: number) => request<{ thread: { id: number; other: any }; messages: any[] }>(`/dm/${id}`),
  dmSend: (data: { to_kind?: 'user' | 'company'; to_id?: number; to_company_id?: number; to_user_id?: number; body: string; as?: 'company' | 'personal' }) =>
    request<{ ok: boolean; thread_id: number }>('/dm', { method: 'POST', body: JSON.stringify(data) }),

  // Upload — multipart/form-data, do NOT set Content-Type (browser sets boundary)
  uploadImage: async (file: File): Promise<{ url: string; key: string }> => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/upload/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const data = await res.json() as { url: string; key: string; error?: string };
    if (!res.ok) throw new Error(data.error || `Upload failed: ${res.status}`);
    return data;
  },

  // Team
  getTeamMembers: () => request<{ members: any[] }>('/team/members'),
  getInvites: () => request<{ invites: any[] }>('/team/invites'),
  createInvite: (email: string, role: string) =>
    request<{ ok: boolean; token: string }>('/team/invites', { method: 'POST', body: JSON.stringify({ email, role }) }),
  revokeInvite: (token: string) =>
    request<{ ok: boolean }>(`/team/invites/${token}`, { method: 'DELETE' }),
  updateMemberRole: (id: number, role: string) =>
    request<{ ok: boolean }>(`/team/members/${id}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  setMemberCompanyPosting: (id: number, allowed: boolean) =>
    request<{ ok: boolean }>(`/team/members/${id}`, { method: 'PATCH', body: JSON.stringify({ can_post_as_company: allowed }) }),
  removeMember: (id: number) =>
    request<{ ok: boolean }>(`/team/members/${id}`, { method: 'DELETE' }),

  // Invite acceptance (public)
  getInvite: (token: string) =>
    request<{ email: string; role: string; company_name: string }>(`/auth/invite/${token}`),
  acceptInvite: (token: string, password: string, name?: string) =>
    request<{ token: string; user: any }>('/auth/accept-invite', {
      method: 'POST', body: JSON.stringify({ token, password, name }),
    }),

  // Messages (Mail)
  getConversations: (search?: string) => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<{ conversations: any[] }>(`/messages/conversations${qs}`);
  },
  getConversation: (id: number) =>
    request<{ conversation: any; messages: any[]; other_company: any }>(`/messages/conversations/${id}`),
  sendMessage: (data: { to_company_id: number; subject?: string; body: string; attachments_json?: string }) =>
    request<{ ok: boolean; conversation_id: number; message_id: number }>('/messages/conversations', {
      method: 'POST', body: JSON.stringify(data),
    }),
  replyMessage: (convId: number, data: { subject?: string; body: string; attachments_json?: string }) =>
    request<{ ok: boolean; message_id: number }>(`/messages/conversations/${convId}/reply`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  getUnreadCounts: () =>
    request<{ mail: number; chat: number }>('/messages/unread'),
  getConversationByCompany: (companyId: number) =>
    request<{ conversation_id: number | null }>(`/messages/conversation-by-company/${companyId}`),

  // Social — connection graph (LinkedIn-style requests) + discovery/feed
  followCompany: (id: number, message?: string) =>
    request<{ ok: boolean; connection: ConnectionState }>(`/social/follow/${id}`, {
      method: 'POST', body: JSON.stringify(message ? { message } : {}),
    }),
  unfollowCompany: (id: number) =>
    request<{ ok: boolean; connection: ConnectionState }>(`/social/follow/${id}`, { method: 'DELETE' }),
  acceptConnection: (id: number) =>
    request<{ ok: boolean; connection: ConnectionState }>(`/social/follow/${id}/accept`, { method: 'POST', body: '{}' }),
  declineConnection: (id: number) =>
    request<{ ok: boolean; connection: ConnectionState }>(`/social/follow/${id}/decline`, { method: 'POST', body: '{}' }),
  getConnectionRequests: () =>
    request<{ incoming: any[]; outgoing: any[] }>('/social/connections/requests'),
  getFollowStatus: (id: number) =>
    request<{ connection: ConnectionState; request_message?: string | null; i_follow: boolean; they_follow: boolean; mutual: boolean }>(`/social/follow/status/${id}`),
  getFollowCounts: () =>
    request<{ following: number; followers: number; pending_incoming?: number; pending_outgoing?: number }>('/social/follow/counts'),
  getFollowing: (params?: Record<string, string>) =>
    request<{ companies: any[]; limit: number; offset: number }>(`/social/following${qs(params)}`),
  getFollowers: (params?: Record<string, string>) =>
    request<{ companies: any[]; limit: number; offset: number }>(`/social/followers${qs(params)}`),
  listSocialCompanies: (params?: Record<string, string>) =>
    request<{ companies: any[]; total: number; limit: number; offset: number }>(`/social/companies${qs(params)}`),
  getFeed: (params?: Record<string, string>) =>
    request<{ lots: any[]; next_cursor: string | null }>(`/social/feed${qs(params)}`),

  // Social posts — feed of user/company posts, likes, comments, reposts, profiles
  getPosts: (params?: Record<string, string>) =>
    request<{ posts: any[]; next_cursor: string | null }>(`/social/posts${qs(params)}`),
  getPost: (id: number) => request<{ post: any }>(`/social/posts/${id}`),
  createPost: (data: { body?: string; images_json?: string[]; kind?: string; as_company?: boolean; repost_of?: number }) =>
    request<{ ok: boolean; id: number }>('/social/posts', { method: 'POST', body: JSON.stringify(data) }),
  deletePost: (id: number) => request<{ ok: boolean }>(`/social/posts/${id}`, { method: 'DELETE' }),
  likePost: (id: number) => request<{ ok: boolean; liked: boolean; like_count: number }>(`/social/posts/${id}/like`, { method: 'POST', body: '{}' }),
  unlikePost: (id: number) => request<{ ok: boolean; liked: boolean; like_count: number }>(`/social/posts/${id}/like`, { method: 'DELETE' }),
  getComments: (id: number) => request<{ comments: any[] }>(`/social/posts/${id}/comments`),
  addComment: (id: number, body: string) =>
    request<{ ok: boolean; id: number }>(`/social/posts/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteComment: (postId: number, commentId: number) =>
    request<{ ok: boolean }>(`/social/posts/${postId}/comments/${commentId}`, { method: 'DELETE' }),
  repost: (id: number, data?: { body?: string; as_company?: boolean }) =>
    request<{ ok: boolean; id: number }>('/social/posts', { method: 'POST', body: JSON.stringify({ repost_of: id, ...(data || {}) }) }),
  getProfile: (userId: number) => request<{ profile: any; posts: any[]; experiences: any[] }>(`/social/profile/${userId}`),
  updateMyProfile: (data: { headline?: string; bio?: string }) =>
    request<{ ok: boolean }>('/social/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  // Work experience (personal profile)
  addExperience: (data: { title: string; company_name: string; location?: string; start_year?: number; end_year?: number; current?: boolean; description?: string }) =>
    request<{ ok: boolean; id: number }>('/social/experience', { method: 'POST', body: JSON.stringify(data) }),
  updateExperience: (id: number, data: { title: string; company_name: string; location?: string; start_year?: number; end_year?: number; current?: boolean; description?: string }) =>
    request<{ ok: boolean }>(`/social/experience/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteExperience: (id: number) => request<{ ok: boolean }>(`/social/experience/${id}`, { method: 'DELETE' }),

  // Chat (Live) — per-user scope
  sendChatRequest: (recipientUserId: number) =>
    request<{ ok: boolean; chat_request_id: number; already_active?: boolean }>('/chat/requests', {
      method: 'POST', body: JSON.stringify({ recipient_user_id: recipientUserId }),
    }),
  getChatRequests: () =>
    request<{ requests: any[] }>('/chat/requests'),
  respondChatRequest: (id: number, status: 'accepted' | 'declined') =>
    request<{ ok: boolean }>(`/chat/requests/${id}`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    }),
  getChatMessages: (requestId: number, after?: string) => {
    const qs = after ? `?after=${encodeURIComponent(after)}` : '';
    return request<{ messages: any[]; other_online: boolean; status: string }>(`/chat/requests/${requestId}/messages${qs}`);
  },
  sendChatMessage: (requestId: number, body: string) =>
    request<{ ok: boolean; message_id: number }>(`/chat/requests/${requestId}/messages`, {
      method: 'POST', body: JSON.stringify({ body }),
    }),

  // Upload — avatar, company logo, attachment
  uploadAvatar: async (file: File): Promise<{ url: string }> => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/upload/avatar`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any).error || `Upload failed: ${res.status}`);
    return data as { url: string };
  },
  uploadCompanyLogo: async (file: File): Promise<{ url: string }> => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/upload/company-logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any).error || `Upload failed: ${res.status}`);
    return data as { url: string };
  },
  uploadAttachment: async (file: File): Promise<{ url: string; key: string; filename: string; size: number; contentType?: string }> => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/upload/attachment`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any).error || `Upload failed: ${res.status}`);
    return data as { url: string; key: string; filename: string; size: number; contentType?: string };
  },

  // ===== Platform admin console =====
  admin: {
    me: () => request<{ admin: { role: string; source: string; email: string; userId: number | null } }>('/admin/me'),
    summary: () => request<{ counts: Record<string, number>; recent_activity: any[]; recent_signups: any[] }>('/admin/summary'),

    users: (params?: Record<string, string>) =>
      request<{ users: any[]; total: number; limit: number; offset: number }>(`/admin/users${qs(params)}`),
    updateUser: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    companies: (params?: Record<string, string>) =>
      request<{ companies: any[]; total: number; limit: number; offset: number }>(`/admin/companies${qs(params)}`),
    updateCompany: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/companies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    lots: (params?: Record<string, string>) =>
      request<{ lots: any[]; total: number; limit: number; offset: number }>(`/admin/lots${qs(params)}`),
    updateLot: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/lots/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteLot: (id: number, reason: string, hard = false) =>
      request<{ ok: boolean }>(`/admin/lots/${id}${hard ? '?hard=1' : ''}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
    restoreLot: (id: number) =>
      request<{ ok: boolean }>(`/admin/lots/${id}/restore`, { method: 'POST', body: JSON.stringify({}) }),

    leads: (params?: Record<string, string>) =>
      request<{ leads: any[]; total: number; limit: number; offset: number }>(`/admin/leads${qs(params)}`),
    updateLead: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    feedback: (params?: Record<string, string>) =>
      request<{ feedback: any[]; total: number; limit: number; offset: number }>(`/admin/feedback${qs(params)}`),
    updateFeedback: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    auditLog: (params?: Record<string, string>) =>
      request<{ entries: any[]; total: number; limit: number; offset: number }>(`/admin/audit-log${qs(params)}`),

    admins: () => request<{ admins: any[]; env_admins: string[] }>('/admin/admins'),
    grantAdmin: (email: string, role: string, reason?: string) =>
      request<{ ok: boolean }>('/admin/admins', { method: 'POST', body: JSON.stringify({ email, role, reason }) }),
    updateAdmin: (id: number, data: Record<string, unknown>) =>
      request<{ ok: boolean }>(`/admin/admins/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    revokeAdmin: (id: number, reason?: string) =>
      request<{ ok: boolean }>(`/admin/admins/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),

    exportData: async (): Promise<void> => {
      const token = getToken();
      const res = await fetch(`${BASE}/admin/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      a.download = cd.match(/filename="(.+?)"/)?.[1] || 'matchmarble-data.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
};

function qs(params?: Record<string, string>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  return entries.length ? '?' + new URLSearchParams(Object.fromEntries(entries)).toString() : '';
}
